import { ChevronRight } from '@mui/icons-material'
import { Box, Tooltip, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { forwardRef } from 'react'

import { categoryIcons } from '../icons'
import type { SearchCategory, SearchResult } from '../types'
import { QuickSearchListItem } from './QuickSearchListItem'

export function getActionLabel(category: SearchCategory, t: ReturnType<typeof useTranslations<'quickSearch'>>) {
  switch (category) {
    case 'charger':
    case 'site':
    case 'vehicle':
    case 'user':
    case 'synoplink':
    case 'solarArray':
      return t('actions.details')
    case 'org':
      return t('actions.launch')
    case 'page':
      return ''
  }
}

export type QuickSearchResultItemProps = {
  result: SearchResult
  selected: boolean
  actionLabel: string
  onSelect: () => void
}

export const QuickSearchResultItem = forwardRef<HTMLLIElement, QuickSearchResultItemProps>(
  function QuickSearchResultItem({ result, selected, actionLabel, onSelect }, ref) {
    const t = useTranslations('quickSearch')

    return (
      <QuickSearchListItem
        ref={ref}
        selected={selected}
        onSelect={onSelect}
        icon={result.icon ?? categoryIcons[result.category]}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, color: 'accent.main' }}>
            <Typography color="inherit" variant="body2">
              {actionLabel}
            </Typography>
            <ChevronRight fontSize="small" />
          </Box>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Tooltip
            title={result.fullLabel ? `${result.fullLabel} / ${result.label}` : ''}
            placement="bottom-start"
            disableHoverListener={!result.fullLabel}
          >
            <Typography variant="body2" noWrap>
              {result.subLabel && (
                <Typography component="span" variant="body2" sx={{ color: 'text.disabled' }}>
                  {result.subLabel}
                  {result.category === 'org' && ' / '}
                  {result.category === 'page' && ': '}
                </Typography>
              )}
              {result.label}
            </Typography>
          </Tooltip>
          {result.matchedOn && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
              {t(`matchedOn.${result.matchedOn.field}`)}: {result.matchedOn.value}
            </Typography>
          )}
        </Box>
      </QuickSearchListItem>
    )
  }
)
